import React, { useRef, useState } from "react"
import { ActionItem } from "../types"
import { Chevron } from "./Chevron"

type Props = {
  items: ActionItem[]
  label?: string
}

let menuSeq = 0

// Row "Actions" dropdown. Uses the same chevron + dropdown surface (shadow,
// placement, "Actions" heading) as the column-header menus, just tinted to the
// CTA text colour. Toggles on click; arrow keys move between items and Escape
// closes and returns focus to the trigger.
export const RowActionsMenu: React.FC<Props> = ({ items, label = "Actions" }) => {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const menuId = useRef(`wb-actions-${menuSeq++}`).current

  if (items.length === 0) return null

  const close = (focusTrigger = false) => {
    setOpen(false)
    if (focusTrigger) triggerRef.current?.focus()
  }

  const focusItem = (index: number) => {
    const buttons = menuRef.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)")
    if (!buttons || buttons.length === 0) return
    const i = (index + buttons.length) % buttons.length
    buttons[i]?.focus()
  }

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault()
      close(true)
      return
    }
    const buttons = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? [])
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement)
    if (e.key === "ArrowDown") {
      e.preventDefault()
      focusItem(current + 1)
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      focusItem(current - 1)
    }
  }

  return (
    <div className="fsp-admin__actions">
      <button
        ref={triggerRef}
        type="button"
        className="fsp-admin__actions-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-controls={open ? menuId : undefined}
        aria-expanded={open}
      >
        {label}
        <Chevron open={open} />
      </button>

      {open && <div className="fsp-admin__hmenu-backdrop" onClick={() => close()} />}

      {open && (
        <div
          ref={menuRef}
          id={menuId}
          className="fsp-admin__hmenu fsp-admin__hmenu--actions"
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKeyDown}
        >
          <div className="fsp-admin__hmenu-section">
            <div className="fsp-admin__hmenu-heading">{label}</div>
            {items.map((item, i) => (
              <button
                key={i}
                type="button"
                role="menuitem"
                className={`fsp-admin__hmenu-item ${item.danger ? "fsp-admin__hmenu-item--danger" : ""}`}
                disabled={item.disabled}
                onClick={() => {
                  item.onClick()
                  close(true)
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
